import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueue } from "../contexts/QueueContext";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { useDocumentStore } from "../store/documentState";
import { useDealsStore } from "../store/dealsStore";
import { uploadFilesAsync as uploadFilesToDrive, base64ToBytes } from "../services/api/drive";
import type { ProjectStatusSnapshot } from "../types/hubspot";
import { getSharedDriveName } from "../utils/env";
import { getProjektdokumentationTemplate, upsertProjektdokumentation, updateProjektdokumentationStatus } from "../services/api/projektdokumentation";
import { submitVotReport, listVotDriveFiles } from "../services/api/vot";
import type { VotDriveFile, VotReportEntry } from "../interfaces/vot";
import {
  ProjektfreigabeStatus,
  ProjektdokumentationChecklistItem,
  ProjektdokumentationFormData,
  ProjektdokumentationSection,
  ProjektdokumentationStoredRecord,
  ProjektdokumentationTemplate,
  ProjektdokumentationUploadTarget,
} from "../types/projektdokumentation";
import {
  getSectionProgress,
  isChecklistItemCompleted,
  isChecklistItemVisible,
  validateProjektdokumentation,
} from "./projektdokumentationValidation";
import {
  getNextVotPhotoFileNames,
  getVotSectionPhotoPrefix,
  isVotPhotoFileNameForPrefix,
  isVotPhotoSectionSupported,
  VOT_PHOTO_DIRECTORIES,
  VOT_PHOTO_JPEG_QUALITY,
  VOT_PHOTO_MAX_DIMENSION,
} from "../utils/votPhotoNaming";

interface ProjektdokumentationWizardProps {
  projectId: string;
  phaseId: string;
  disabled?: boolean;
  onCompleted?: (completedAt: string) => void;
}

interface LocalDraftState {
  sections: ProjektdokumentationSection[];
  technicalQuestionsOpen: boolean;
  tightestAccessCm: string;
  heatingRoomAreaApprox: string;
  lowCeilingHeightNote: string;
  pvStatus: "no" | "existing" | "planned" | "";
  drainAvailable: "" | "yes" | "no";
  status: ProjektfreigabeStatus;
}

const statusLabels: Record<ProjektfreigabeStatus, string> = {
  draft: "Entwurf",
  in_review: "In Prüfung",
  ready: "Freigabefähig",
  approved: "Freigegeben",
};

const statusTone: Record<ProjektfreigabeStatus, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
  in_review: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  ready: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200",
};

const emptyDraftState = (): LocalDraftState => ({
  sections: [],
  technicalQuestionsOpen: false,
  tightestAccessCm: "",
  heatingRoomAreaApprox: "",
  lowCeilingHeightNote: "",
  pvStatus: "",
  drainAvailable: "",
  status: "draft",
});

const toDraftState = (
  template: ProjektdokumentationTemplate
): LocalDraftState => {
  const record = template.existingRecord;
  return {
    sections: record?.sections || template.sections,
    technicalQuestionsOpen: record?.technicalQuestionsOpen || false,
    tightestAccessCm:
      typeof record?.tightestAccessCm === "number" ? String(record.tightestAccessCm) : "",
    heatingRoomAreaApprox: record?.heatingRoomAreaApprox || "",
    lowCeilingHeightNote: record?.lowCeilingHeightNote || "",
    pvStatus: record?.pvStatus || "",
    drainAvailable:
      typeof record?.drainAvailable === "boolean"
        ? record.drainAvailable
          ? "yes"
          : "no"
        : "",
    status: record?.status || "draft",
  };
};

const buildPayload = (
  projectId: string,
  draft: LocalDraftState,
  user: { id: string; email: string } | null
): ProjektdokumentationFormData => ({
  // technicalQuestionsOpen wird primär aus dem Checklist-Item abgeleitet,
  // damit Anzeige- und Persistenzzustand nicht auseinanderlaufen.
  projectId,
  sections: draft.sections,
  technicalQuestionsOpen: (() => {
    const item = draft.sections
      .flatMap((section) => section.items)
      .find((entry) => entry.id === "technical-questions-status");
    if (typeof item?.valueBoolean === "boolean") {
      return !item.valueBoolean;
    }
    return draft.technicalQuestionsOpen;
  })(),
  tightestAccessCm: /^\d+$/.test(draft.tightestAccessCm) ? Number(draft.tightestAccessCm) : null,
  heatingRoomAreaApprox: draft.heatingRoomAreaApprox.trim() || undefined,
  lowCeilingHeightNote: draft.lowCeilingHeightNote.trim() || undefined,
  pvStatus: draft.pvStatus || null,
  drainAvailable:
    draft.drainAvailable === "yes" ? true : draft.drainAvailable === "no" ? false : null,
  updatedByEmail: user?.email ?? "",
  updatedByUserId: user?.id ?? "",
});

const formatItemValue = (item: ProjektdokumentationChecklistItem): string | null => {
  switch (item.inputType) {
    case "text":
      return item.valueString?.trim() || null;
    case "select": {
      const value = item.valueString?.trim();
      if (!value) return null;
      return item.options?.find((option) => option.value === value)?.label || value;
    }
    case "number":
      return typeof item.valueNumber === "number" && Number.isFinite(item.valueNumber)
        ? `${item.valueNumber}${item.inputLabel ? ` ${item.inputLabel}` : ""}`
        : null;
    case "boolean":
      return typeof item.valueBoolean === "boolean" ? (item.valueBoolean ? "Ja" : "Nein") : null;
    case "url":
      return item.valueUrl?.trim() || null;
    default:
      return null;
  }
};

const VOT_REPORT_SUPPRESSED_ITEM_IDS = new Set([
  // Der Wert ist identisch zu "tightest-access-recorded" und soll im Bericht nicht doppelt erscheinen.
  "tightest-access-confirmed",
]);

const VOT_REPORT_QUESTION_LABELS: Record<string, string> = {
  "tightest-access-recorded": "Engste Stelle Zuweg / Einbringung",
  "heating-room-area-recorded": "Ungefähre Fläche Heizraum",
  "drain-availability-recorded": "Abfluss im Heizraum / Keller",
  "low-ceiling-height-present": "Niedrige Deckenhöhe vorhanden",
  "low-ceiling-height-documented": "Niedrige Deckenhöhe dokumentiert",
  "pv-status-recorded": "PV-Anlage vorhanden/geplant",
  "technical-questions-status": "Offene technische Fragen",
};

const VOT_REPORT_VALUE_UNITS: Record<string, string> = {
  "tightest-access-recorded": "cm",
  "tightest-access-confirmed": "cm",
  "heating-room-area-recorded": "m²",
};

const getVotReportQuestion = (item: ProjektdokumentationChecklistItem): string =>
  VOT_REPORT_QUESTION_LABELS[item.id] || item.label;

const valueIncludesUnit = (value: string, unit: string): boolean => {
  if (unit === "m²") {
    return /(?:^|[\d\s])(?:m²|m2|qm|quadratmeter)(?=$|[\s.,;)])/i.test(value);
  }

  return /(?:^|[\d\s])cm(?=$|[\s.,;)])/i.test(value);
};

const formatVotReportMeasuredValue = (
  item: ProjektdokumentationChecklistItem,
  value: string
): string => {
  const unit = VOT_REPORT_VALUE_UNITS[item.id];
  if (!unit || valueIncludesUnit(value, unit)) {
    return value;
  }

  return `${value} ${unit}`;
};

const formatVotBooleanValue = (item: ProjektdokumentationChecklistItem): string | null => {
  if (typeof item.valueBoolean !== "boolean") {
    return null;
  }

  if (item.id === "technical-questions-status") {
    return item.valueBoolean
      ? "Nein, keine offenen technischen Fragen"
      : "Ja, offene technische Fragen vorhanden";
  }

  return item.valueBoolean ? "Ja" : "Nein";
};

const formatVotReportValue = (item: ProjektdokumentationChecklistItem): string | null => {
  if (item.inputType === "boolean") {
    const value = formatVotBooleanValue(item);
    return value ? `Antwort: ${value}` : null;
  }

  if (item.inputType === "select") {
    const value = formatItemValue(item);
    return value ? `Antwort: ${value}` : null;
  }

  if (item.inputType === "url") {
    const value = formatItemValue(item);
    return value ? `Link: ${value}` : null;
  }

  const value = formatItemValue(item);
  return value ? `Wert: ${formatVotReportMeasuredValue(item, value)}` : null;
};

const buildVotReportEntries = (
  data: ProjektdokumentationFormData,
  hasPhotosForSection: (section: ProjektdokumentationSection) => boolean = () => false
): VotReportEntry[] => {
  const entries: VotReportEntry[] = [];

  data.sections.forEach((section) => {
    const sectionHasPhotos =
      isVotPhotoSectionSupported(section.id, section.code) && hasPhotosForSection(section);

    section.items.forEach((item) => {
      if (
        VOT_REPORT_SUPPRESSED_ITEM_IDS.has(item.id) ||
        !isChecklistItemVisible(data, section.id, item.id)
      ) {
        return;
      }

      const parts: string[] = [];
      const value = formatVotReportValue(item);
      const comment = item.comment?.trim();
      if (value) parts.push(value);
      if (comment) parts.push(`Bemerkung: ${comment}`);

      if (parts.length === 0) {
        return;
      }

      entries.push({
        section: `[${section.code}] ${section.title}`,
        sectionId: section.id,
        sectionCode: section.code,
        itemId: item.id,
        question: getVotReportQuestion(item),
        answer: parts.join("\n"),
      });
    });

    if (sectionHasPhotos) {
      entries.push({
        section: `[${section.code}] ${section.title}`,
        sectionId: section.id,
        sectionCode: section.code,
        question: "Fotodokumentation",
        answer: "Bilddokumentation",
        photoPrefix: getVotSectionPhotoPrefix(section),
      });
    }
  });

  if (data.lowCeilingHeightNote?.trim()) {
    entries.push({
      section: "[K] Technische Angaben",
      question: "Niedrige Deckenhöhe - Notiz",
      answer: `Bemerkung: ${data.lowCeilingHeightNote.trim()}`,
    });
  }

  return entries;
};

const sortVotDriveFiles = (files: VotDriveFile[]): VotDriveFile[] =>
  [...files].sort((a, b) => a.name.localeCompare(b.name, "de"));

const createImageElement = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Bild "${file.name}" konnte nicht gelesen werden`));
    };
    image.src = url;
  });

const normalizeImageForVotUpload = async (file: File, fileName: string): Promise<File> => {
  const image = await createImageElement(file);
  const scale = Math.min(
    1,
    VOT_PHOTO_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight)
  );
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Bild konnte nicht verarbeitet werden");
  }

  context.drawImage(image, 0, 0, width, height);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error("Bild konnte nicht als JPEG exportiert werden"));
      },
      "image/jpeg",
      VOT_PHOTO_JPEG_QUALITY
    );
  });

  return new File([blob], fileName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const base64ToFile = (base64Content: string, fileName: string, mimeType: string): File => {
  return new File([base64ToBytes(base64Content)], fileName, { type: mimeType });
};

const updateBoundFieldsForItem = (
  previous: LocalDraftState,
  itemId: string,
  item: ProjektdokumentationChecklistItem
): Partial<LocalDraftState> => {
  switch (itemId) {
    case "tightest-access-recorded":
    case "tightest-access-confirmed":
      return {
        tightestAccessCm:
          typeof item.valueNumber === "number" ? String(item.valueNumber) : previous.tightestAccessCm,
      };
    case "heating-room-area-recorded":
      return { heatingRoomAreaApprox: item.valueString || previous.heatingRoomAreaApprox };
    case "low-ceiling-height-recorded":
      return { lowCeilingHeightNote: item.valueString || previous.lowCeilingHeightNote };
    case "pv-status-recorded":
      return {
        pvStatus:
          item.valueString === "no" || item.valueString === "existing" || item.valueString === "planned"
            ? item.valueString
            : previous.pvStatus,
      };
    case "drain-availability-recorded":
      return {
        drainAvailable:
          typeof item.valueBoolean === "boolean"
            ? item.valueBoolean
              ? "yes"
              : "no"
            : previous.drainAvailable,
      };
    case "technical-questions-status":
      return {
        technicalQuestionsOpen:
          typeof item.valueBoolean === "boolean" ? !item.valueBoolean : previous.technicalQuestionsOpen,
      };
    default:
      return {};
  }
};

const ProjektdokumentationWizard: React.FC<ProjektdokumentationWizardProps> = ({
  projectId,
  phaseId,
  disabled = false,
  onCompleted,
}) => {
  const { addToQueue } = useQueue();
  const { showToast } = useToast();
  const { user } = useAuth();
  const updatePhaseStatus = useDocumentStore((state) => state.updatePhaseStatus);
  const upsertDeal = useDealsStore((state) => state.upsertDeal);
  const deals = useDealsStore((state) => state.deals);
  const [template, setTemplate] = useState<ProjektdokumentationTemplate | null>(null);
  const [draft, setDraft] = useState<LocalDraftState>(emptyDraftState);
  const [storedRecord, setStoredRecord] = useState<ProjektdokumentationStoredRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [votDriveFiles, setVotDriveFiles] = useState<VotDriveFile[]>([]);
  const [isLoadingVotDriveFiles, setIsLoadingVotDriveFiles] = useState(false);
  const [votPhotoBusy, setVotPhotoBusy] = useState<Record<string, string>>({});
  const [votPhotoErrors, setVotPhotoErrors] = useState<Record<string, string>>({});
  const [showMissingRequiredHighlights, setShowMissingRequiredHighlights] = useState(false);
  const autosaveRef = useRef<number | null>(null);

  const draftStorageKey = useMemo(
    () => `projektdokumentation-draft:${projectId}:${phaseId}`,
    [phaseId, projectId]
  );

  const activeDeal = useMemo(
    () => deals.find((deal) => deal.id === projectId) || null,
    [deals, projectId]
  );
  const driveDealName = useMemo(() => {
    const pathLabel = template?.uploadTargetInfo?.projectFolder?.pathLabel;
    const fromPath = pathLabel?.split("/")?.[0]?.trim();
    return activeDeal?.dealname || fromPath || projectId;
  }, [activeDeal?.dealname, projectId, template?.uploadTargetInfo?.projectFolder?.pathLabel]);
  const gdriveFolderId = activeDeal?.gdrive_folder_id || undefined;
  const payload = useMemo(() => buildPayload(projectId, draft, user), [draft, projectId, user]);
  const validation = useMemo(() => validateProjektdokumentation(payload), [payload]);
  const currentStatus = storedRecord?.status || draft.status;
  const isApproved = currentStatus === "approved";
  const isReadOnly = disabled || isApproved;
  const hasPendingVotPhotoUploads = Object.keys(votPhotoBusy).length > 0;
  const statusAction =
    currentStatus !== "approved"
      ? {
          label: "Abschließen",
          nextStatus: "approved" as const,
          className: "bg-green-600 text-white",
          disabled: hasPendingVotPhotoUploads || validation.missingRequiredItems.length > 0,
        }
      : null;
  const missingRequiredItems = useMemo(() => {
    const sourceItems = validation.missingRequiredItems;
    const uniqueItems = Array.from(
      new Set(
        sourceItems
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      )
    );

    return uniqueItems;
  }, [validation.missingRequiredItems]);
  const missingRequiredItemIds = useMemo(
    () => new Set(validation.missingRequiredItemIds),
    [validation.missingRequiredItemIds]
  );
  const isDirectChoiceItem = (item: ProjektdokumentationChecklistItem) =>
    item.inputType === "boolean" || item.inputType === "select";
  const isVisibleInCurrentPayload = (sectionId: string, itemId: string) =>
    isChecklistItemVisible(payload, sectionId, itemId);
  const getVotPhotoKey = (section: ProjektdokumentationSection) => `section:${section.id}`;
  const getVotPhotosForSection = (section: ProjektdokumentationSection) => {
    const prefix = getVotSectionPhotoPrefix(section);
    return sortVotDriveFiles(
      votDriveFiles.filter((file) => isVotPhotoFileNameForPrefix(prefix, file.name))
    );
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  };

  const getBulkCompletedItem = (item: ProjektdokumentationChecklistItem) => {
    if (item.id === "low-ceiling-height-present") {
      // Bulk-Complete darf hier nicht auf "true" defaulten (fälschliche Behauptung,
      // es gäbe eine niedrige Deckenhöhe) — aber das Item muss trotzdem als
      // "checked" gelten, sonst kann die Sektion nie als vollständig markiert werden.
      // Default ist daher "false" (kein Vorkommen), vom Monteur weiterhin änderbar.
      return {
        ...item,
        checked: true,
        valueBoolean: typeof item.valueBoolean === "boolean" ? item.valueBoolean : false,
      };
    }

    if (item.inputType === "boolean") {
      return {
        ...item,
        checked: true,
        valueBoolean: typeof item.valueBoolean === "boolean" ? item.valueBoolean : true,
      };
    }

    if (item.inputType === "select") {
      return {
        ...item,
        checked: true,
        valueString: item.valueString || item.options?.[0]?.value || "",
      };
    }

    return {
      ...item,
      checked: true,
    };
  };

  const getBulkClearedItem = (item: ProjektdokumentationChecklistItem) => {
    if (item.inputType === "boolean") {
      return {
        ...item,
        checked: false,
        valueBoolean: null,
      };
    }

    if (item.inputType === "select") {
      return {
        ...item,
        checked: false,
        valueString: "",
      };
    }

    return {
      ...item,
      checked: false,
    };
  };

  const setSectionCompleted = (sectionId: string, checked: boolean) => {
    if (isReadOnly) {
      return;
    }

    setDraft((previous) => {
      let nextTechnicalQuestionsOpen = previous.technicalQuestionsOpen;
      let nextDrainAvailable = previous.drainAvailable;
      let nextPvStatus = previous.pvStatus;
      const previousPayload = buildPayload(projectId, previous, user);

      const sections = previous.sections.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const visibleItemIds = new Set(
          section.items
            .filter((item) => isChecklistItemVisible(previousPayload, section.id, item.id))
            .map((item) => item.id)
        );

        return {
          ...section,
          items: section.items.map((item) => {
            if (!visibleItemIds.has(item.id)) {
              return item;
            }

            const nextItem = checked ? getBulkCompletedItem(item) : getBulkClearedItem(item);

            if (item.id === "technical-questions-status") {
              nextTechnicalQuestionsOpen =
                typeof nextItem.valueBoolean === "boolean"
                  ? !nextItem.valueBoolean
                  : previous.technicalQuestionsOpen;
            }

            if (item.id === "drain-availability-recorded") {
              nextDrainAvailable =
                typeof nextItem.valueBoolean === "boolean"
                  ? nextItem.valueBoolean
                    ? "yes"
                    : "no"
                  : "";
            }

            if (item.id === "pv-status-recorded") {
              nextPvStatus =
                nextItem.valueString === "no" ||
                nextItem.valueString === "existing" ||
                nextItem.valueString === "planned"
                  ? nextItem.valueString
                  : "";
            }

            return nextItem;
          }),
        };
      });

      return {
        ...previous,
        sections,
        technicalQuestionsOpen: nextTechnicalQuestionsOpen,
        drainAvailable: nextDrainAvailable,
        pvStatus: nextPvStatus,
      };
    });
  };

  const openResolvedFolder = async (target: ProjektdokumentationUploadTarget) => {
    if (target.url) {
      window.open(target.url, "_blank", "noopener,noreferrer");
    }
  };

  const renderUploadTarget = (target: ProjektdokumentationUploadTarget) => (
    <div key={target.label} className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {target.label}
      </p>
      {target.url ? (
        <button
          type="button"
          onClick={() => void openResolvedFolder(target)}
          className="mt-2 inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-300"
        >
          Ordner öffnen
        </button>
      ) : (
        <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          Uploads laufen über die bestehende Projekt-/Drive-Struktur
        </p>
      )}
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 break-words">
        {target.pathLabel}
      </p>
    </div>
  );

  const syncDealProjectStatus = (
    projectStatus: Partial<ProjectStatusSnapshot> | undefined,
    record?: ProjektdokumentationStoredRecord | null
  ) => {
    const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === projectId);
    if (!existingDeal) {
      return;
    }

    const existingSnapshot = existingDeal.project_status || null;
    // API liefert bei einem Status-Update einen vollständigen Snapshot zurück (kein
    // partielles Patch) — die Merge-Reihenfolge erhält vorhandene Felder als Fallback.
    const nextSnapshot = projectStatus
      ? ({
          ...(existingSnapshot || {}),
          ...projectStatus,
        } as ProjectStatusSnapshot)
      : existingSnapshot
        ? { ...existingSnapshot }
        : null;

    if (nextSnapshot && record?.status) {
      nextSnapshot.projektdokumentationApprovalStatus = record.status;
    }

    upsertDeal({
      ...existingDeal,
      project_status: nextSnapshot,
    });
  };

  useEffect(() => {
    let mounted = true;

    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getProjektdokumentationTemplate(projectId);
        if (!mounted) {
          return;
        }

        setTemplate(response);
        setStoredRecord(response.existingRecord);
        setExpandedSections(
          Object.fromEntries(response.sections.map((section) => [section.id, true]))
        );

        const fromServer = toDraftState(response);
        const rawDraft = window.localStorage.getItem(draftStorageKey);
        if (rawDraft && !response.existingRecord?.approvedAt) {
          try {
            const parsed = JSON.parse(rawDraft) as LocalDraftState;
            setDraft({
              ...fromServer,
              ...parsed,
            });
          } catch {
            setDraft(fromServer);
          }
        } else {
          setDraft(fromServer);
        }

        if (response.existingRecord?.status === "approved") {
          const approvedAt = response.existingRecord.approvedAt || response.existingRecord.updatedAt;
          updatePhaseStatus(
            phaseId,
            "completed",
            approvedAt || undefined,
            response.existingRecord.completedByEmail || undefined
          );
          onCompleted?.(approvedAt || response.existingRecord.updatedAt);
        } else {
          updatePhaseStatus(phaseId, "pending");
        }

        setHydrated(true);
      } catch (loadError) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Projektdokumentation konnte nicht geladen werden"
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTemplate();
    return () => {
      mounted = false;
    };
  }, [draftStorageKey, phaseId, projectId, updatePhaseStatus]);

  useEffect(() => {
    if (!template || !hydrated) {
      return;
    }

    let mounted = true;
    const loadVotFiles = async () => {
      try {
        setIsLoadingVotDriveFiles(true);
        const response = await listVotDriveFiles(projectId);
        if (mounted) {
          setVotDriveFiles(sortVotDriveFiles(response.files || []));
          setVotPhotoErrors({});
        }
      } catch (loadError) {
        if (mounted) {
          setVotPhotoErrors({
            __global:
              loadError instanceof Error
                ? loadError.message
                : "VOT-Bilder konnten nicht geladen werden",
          });
        }
      } finally {
        if (mounted) {
          setIsLoadingVotDriveFiles(false);
        }
      }
    };

    void loadVotFiles();
    return () => {
      mounted = false;
    };
  }, [hydrated, projectId, template]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    if (isApproved) {
      window.localStorage.removeItem(draftStorageKey);
      return;
    }

    window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
  }, [draft, draftStorageKey, hydrated, isApproved]);

  useEffect(() => {
    if (!template || !hydrated || isReadOnly) {
      return;
    }

    if (autosaveRef.current) {
      window.clearTimeout(autosaveRef.current);
    }

    autosaveRef.current = window.setTimeout(() => {
      const save = async () => {
        try {
          if (navigator.onLine) {
            const response = await upsertProjektdokumentation(projectId, payload);
            setStoredRecord(response.record || null);
            syncDealProjectStatus(response.projectStatus, response.record || null);
            if (response.record?.status && response.record.status !== draft.status) {
              setDraft((previous) => ({
                ...previous,
                status: response.record?.status || previous.status,
              }));
            }
          } else {
            await addToQueue("projektdokumentation_upsert", {
              projectId,
              payload,
            });
          }
        } catch (error) {
          const status = (error as any)?.status;
          if (typeof status === 'number' && status >= 400 && status < 500) {
            return; // 4xx = permanent error, nicht in Queue schreiben
          }
          await addToQueue("projektdokumentation_upsert", {
            projectId,
            payload,
          });
        }
      };

      void save();
    }, 750);

    return () => {
      if (autosaveRef.current) {
        window.clearTimeout(autosaveRef.current);
      }
    };
  }, [addToQueue, hydrated, isReadOnly, payload, projectId, template]);

  const setItem = (
    sectionId: string,
    itemId: string,
    updater: (item: ProjektdokumentationChecklistItem) => ProjektdokumentationChecklistItem
  ) => {
    if (isReadOnly) {
      return;
    }

    setDraft((previous) => {
      let nextItemState: ProjektdokumentationChecklistItem | null = null;
      const sections = previous.sections.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const nextSection = {
          ...section,
          items: section.items.map((item) => {
            if (item.id !== itemId) {
              return item;
            }

            nextItemState = updater(item);
            return nextItemState;
          }),
        };

        if (itemId === "drain-availability-recorded" && nextItemState?.valueBoolean === false) {
          nextSection.items = nextSection.items.map((item) =>
            item.id === "drain-documented"
              ? { ...item, checked: false, comment: undefined }
              : item
          );
        }

        if (itemId === "special-challenges-documented" && nextItemState?.checked === false) {
          nextSection.items = nextSection.items.map((item) =>
            item.id === "special-challenges-structural" ||
            item.id === "special-challenges-technical" ||
            item.id === "special-challenges-organizational"
              ? { ...item, checked: false, comment: undefined }
              : item
          );
        }

        return nextSection;
      });

      return {
        ...previous,
        sections,
        ...updateBoundFieldsForItem(previous, itemId, nextItemState || ({} as ProjektdokumentationChecklistItem)),
      };
    });
  };

  const mergeVotDriveFilesForPrefix = (prefix: string, files: VotDriveFile[]) => {
    setVotDriveFiles((previous) =>
      sortVotDriveFiles([
        ...previous.filter((file) => !isVotPhotoFileNameForPrefix(prefix, file.name)),
        ...files,
      ])
    );
  };

  const handleVotPhotoFiles = async (
    section: ProjektdokumentationSection,
    selectedFiles: File[]
  ) => {
    if (!isVotPhotoSectionSupported(section.id, section.code)) {
      return;
    }

    if (isReadOnly || selectedFiles.length === 0) {
      return;
    }

    const imageFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      showToast("error", "Bitte nur Bilddateien auswählen", 3000);
      return;
    }

    const key = getVotPhotoKey(section);
    const prefix = getVotSectionPhotoPrefix(section);
    setVotPhotoBusy((previous) => ({ ...previous, [key]: "uploading" }));
    setVotPhotoErrors((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });

    try {
      const latest = await listVotDriveFiles(projectId, prefix);
      const latestSectionFiles = latest.files.filter((file) =>
        isVotPhotoFileNameForPrefix(prefix, file.name)
      );
      const fileNames = getNextVotPhotoFileNames(
        prefix,
        latestSectionFiles.map((file) => file.name),
        imageFiles.length
      );
      const normalizedFiles = await Promise.all(
        imageFiles.map((file, index) => normalizeImageForVotUpload(file, fileNames[index]))
      );
      const uploadResult = await uploadFilesToDrive(
        normalizedFiles,
        driveDealName,
        [...VOT_PHOTO_DIRECTORIES],
        { sharedDrive: getSharedDriveName(), gdriveFolderId },
      );

      if (!uploadResult.status || !uploadResult.files) {
        throw new Error(uploadResult.message || "VOT-Bilder konnten nicht hochgeladen werden");
      }

      const uploadedFiles: VotDriveFile[] = uploadResult.files.map((file, index) => ({
        id: file.fileId,
        name: file.fileName || normalizedFiles[index].name,
        mimeType: "image/jpeg",
        webViewLink: file.webViewLink,
      }));

      mergeVotDriveFilesForPrefix(prefix, [
        ...latestSectionFiles,
        ...uploadedFiles,
      ]);
      showToast("success", `${uploadedFiles.length} VOT-Bild(er) hochgeladen`, 2500);
    } catch (uploadError) {
      const message =
        uploadError instanceof Error ? uploadError.message : "VOT-Bilder konnten nicht hochgeladen werden";
      setVotPhotoErrors((previous) => ({ ...previous, [key]: message }));
      showToast("error", message, 4000);
    } finally {
      setVotPhotoBusy((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
    }
  };

  const selectVotPhotosFromAlbum = (section: ProjektdokumentationSection) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.style.display = "none";
    input.onchange = () => {
      void handleVotPhotoFiles(section, Array.from(input.files || []));
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  };

  const captureVotPhoto = (section: ProjektdokumentationSection) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.setAttribute("capture", "environment");
    input.style.display = "none";
    input.onchange = () => {
      void handleVotPhotoFiles(section, Array.from(input.files || []));
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  };

  const renderVotPhotoControls = (section: ProjektdokumentationSection) => {
    if (!isVotPhotoSectionSupported(section.id, section.code)) {
      return null;
    }

    const key = getVotPhotoKey(section);
    const photos = getVotPhotosForSection(section);
    const isBusy = !!votPhotoBusy[key];
    const errorMessage = votPhotoErrors[key];

    return (
      <div className="mx-4 mb-4 mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              VOT-Bilder
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {photos.length > 0
                ? `${photos.length} Bild(er) in Bilder/VOT`
                : isLoadingVotDriveFiles
                  ? "Bilder werden geladen..."
                  : "Noch keine Bilder hinterlegt"}
            </p>
          </div>
          {!isReadOnly && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => captureVotPhoto(section)}
                className="rounded-md bg-primary-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
              >
                Foto aufnehmen
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => selectVotPhotosFromAlbum(section)}
                className="rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
              >
                Aus Album wählen
              </button>
            </div>
          )}
        </div>

        {isBusy && (
          <p className="mt-2 text-xs text-primary-700 dark:text-primary-300">
            Bilder werden hochgeladen...
          </p>
        )}
        {errorMessage && (
          <p className="mt-2 text-xs text-red-700 dark:text-red-300">
            {errorMessage}
          </p>
        )}
        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {photos.map((photo) => (
              <a
                key={`${photo.id}-${photo.name}`}
                href={photo.webViewLink}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
              >
                {photo.thumbnailLink ? (
                  <img
                    src={photo.thumbnailLink}
                    alt={photo.name}
                    className="mb-2 h-20 w-full rounded object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="mb-2 flex h-20 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">
                    Bild
                  </div>
                )}
                <span className="block truncate" title={photo.name}>
                  {photo.name}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  const setStatus = async (status: ProjektfreigabeStatus) => {
    if (isReadOnly || isSavingStatus) {
      return;
    }

    if (hasPendingVotPhotoUploads) {
      showToast("error", "Bitte warte, bis alle VOT-Bilder hochgeladen sind", 3000);
      return;
    }

    setIsSavingStatus(true);

    const optimisticApprovedAt = new Date().toISOString();

    const applyLocalStatus = (record?: ProjektdokumentationStoredRecord | null) => {
      if (record) {
        setStoredRecord(record);
        setDraft((previous) => ({
          ...previous,
          status: record.status,
          sections: record.sections,
        }));
      } else {
        setDraft((previous) => ({ ...previous, status }));
      }
    };

    try {
      if (navigator.onLine) {
        const submittedAt = new Date().toISOString();
        let votReportLink: string | null = null;

        if (status === "approved") {
          await upsertProjektdokumentation(projectId, payload);
          const report = await submitVotReport(projectId, {
            projectId,
            submittedAt,
            submittedByEmail: user?.email,
            entries: buildVotReportEntries(
              payload,
              (section) => getVotPhotosForSection(section).length > 0
            ),
          });
          const pdfFile = base64ToFile(
            report.file.base64Content,
            report.file.fileName,
            report.file.mimeType
          );
          const uploadResult = await uploadFilesToDrive(
            [pdfFile],
            report.dealName || projectId,
            report.targetDirectories,
            { sharedDrive: getSharedDriveName(), gdriveFolderId },
          );
          const uploadedFile = uploadResult.files?.[0];
          if (!uploadedFile?.webViewLink) {
            throw new Error("VOT-PDF wurde erzeugt, aber nicht in Google Drive gespeichert");
          }
          votReportLink = uploadedFile.webViewLink;
        }

        const response = await updateProjektdokumentationStatus(projectId, {
          projectId,
          status,
          votReportLink: votReportLink ?? undefined,
        });
        applyLocalStatus(response.record || null);
        syncDealProjectStatus(response.projectStatus, response.record || null);

        if (status === "approved") {
          const approvedAt = response.record?.approvedAt || optimisticApprovedAt;
          updatePhaseStatus(
            phaseId,
            "completed",
            approvedAt,
            response.record?.completedByEmail || undefined
          );
          onCompleted?.(approvedAt);
          showToast("success", "Projektdokumentation freigegeben", 3000);
        } else {
          showToast("success", `Status aktualisiert: ${statusLabels[status]}`, 2500);
        }
      } else {
        applyLocalStatus();
        await addToQueue("projektdokumentation_status_update", {
          projectId,
          phaseId,
          payload: {
            projectId,
            status,
          },
        });

        if (status === "approved") {
          updatePhaseStatus(phaseId, "completed", optimisticApprovedAt);
          onCompleted?.(optimisticApprovedAt);
        }
        showToast("info", "Status wurde offline in die Queue gelegt", 3000);
      }
    } catch (statusError) {
      showToast(
        "error",
        statusError instanceof Error ? statusError.message : "Status konnte nicht aktualisiert werden",
        4000
      );
    } finally {
      setIsSavingStatus(false);
    }
  };

  const requestStatusChange = (status: ProjektfreigabeStatus) => {
    if (status === "approved" && missingRequiredItems.length > 0) {
      setShowMissingRequiredHighlights(true);
      expandMissingRequiredSections();
      return;
    }

    void setStatus(status);
  };

  const expandMissingRequiredSections = () => {
    if (missingRequiredItemIds.size === 0) {
      return;
    }

    setExpandedSections((previous) => ({
      ...previous,
      ...Object.fromEntries(
        payload.sections
          .filter((section) =>
            section.items.some((item) => missingRequiredItemIds.has(item.id))
          )
          .map((section) => [section.id, true])
      ),
    }));
  };

  const renderItemInput = (
    sectionId: string,
    item: ProjektdokumentationChecklistItem,
    isHighlighted = false
  ) => {
    if (!item.inputType || item.inputType === "none") {
      return null;
    }

    const commonClassName =
      `mt-3 w-full rounded-lg border bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white ${
        isHighlighted
          ? "border-red-500 ring-1 ring-red-500 focus:border-red-500 focus:ring-red-500"
          : "border-gray-300 dark:border-gray-700"
      }`;

    if (item.inputType === "text" || item.inputType === "url") {
      const value = item.inputType === "url" ? item.valueUrl || "" : item.valueString || "";
      return (
        <input
          type={item.inputType === "url" ? "url" : "text"}
          value={value}
          disabled={isReadOnly}
          onChange={(event) =>
            setItem(sectionId, item.id, (current) => ({
              ...current,
              [item.inputType === "url" ? "valueUrl" : "valueString"]: event.target.value,
              checked: current.checked || event.target.value.trim().length > 0,
            }))
          }
          className={commonClassName}
          placeholder={item.inputPlaceholder || item.inputLabel || "Eintragen"}
        />
      );
    }

    if (item.inputType === "number") {
      return (
        <input
          type="text"
          inputMode="numeric"
          value={typeof item.valueNumber === "number" ? String(item.valueNumber) : ""}
          disabled={isReadOnly}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw !== "" && !/^\d+$/.test(raw)) {
              return;
            }

            setItem(sectionId, item.id, (current) => ({
              ...current,
              valueNumber: raw === "" ? null : Number(raw),
              checked: current.checked || raw !== "",
            }));
          }}
          className={commonClassName}
          placeholder={item.inputPlaceholder || "Zahl eingeben"}
        />
      );
    }

    if (item.inputType === "boolean") {
      const selectedValue =
        typeof item.valueBoolean === "boolean" ? (item.valueBoolean ? "yes" : "no") : "";
      const booleanOptions =
        item.id === "technical-questions-status"
          ? [
              { value: true, label: "Ja (keine offen)" },
              { value: false, label: "Nein (es gibt offene)" },
            ]
          : [
              { value: true, label: "Ja" },
              { value: false, label: "Nein" },
            ];
      return (
        <div
          className={`mt-3 grid grid-cols-2 gap-3 rounded-lg ${
            isHighlighted ? "border border-red-500 p-1" : ""
          }`}
        >
          {booleanOptions.map((option) => {
            const isSelected =
              typeof item.valueBoolean === "boolean" && item.valueBoolean === option.value;

            return (
              <button
                key={option.label}
                type="button"
                disabled={isReadOnly}
                onClick={() =>
                  setItem(sectionId, item.id, (current) => ({
                    ...current,
                    valueBoolean: option.value,
                    checked: true,
                  }))
                }
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  isSelected
                    ? "border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
                aria-pressed={selectedValue === (option.value ? "yes" : "no")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    if (item.inputType === "select") {
      return (
        <select
          value={item.valueString || ""}
          disabled={isReadOnly}
          onChange={(event) =>
            setItem(sectionId, item.id, (current) => ({
              ...current,
              valueString: event.target.value,
              checked: event.target.value !== "",
            }))
          }
          className={commonClassName}
        >
          {!item.valueString && <option value="">Bitte wählen</option>}
          {(item.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error || "Projektdokumentation konnte nicht geladen werden"}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden">
        <div className="p-5 border-b dark:border-gray-700 bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-800">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-[18rem]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-300">
                Sales → OPS
              </p>
              <h2 className="mt-2 text-xl font-semibold leading-7 text-gray-900 dark:text-white">
                Freigabe-Checkliste
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Unterlagen so vollständig erfassen, dass Planung, Bestellung und Umsetzung direkt
                starten können.
              </p>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusTone[currentStatus]}`}>
                {statusLabels[currentStatus]}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {missingRequiredItems.length === 0
                  ? "Alle Pflichtpunkte erfüllt"
                  : `${missingRequiredItems.length} Pflichtpunkte offen`}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Fortschritt
            </p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {storedRecord?.completenessPercent ?? validation.completenessPercent}%
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Sichtbar
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {currentStatus === "approved"
                ? "Freigegeben"
                : validation.isReadyForApproval
                  ? "Abschlussbereit"
                  : "Unvollständig"}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3 col-span-2">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Letzte Änderung
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
              {storedRecord?.updatedAt
                ? new Date(storedRecord.updatedAt).toLocaleString("de-DE")
                : "Noch nicht gespeichert"}
            </p>
          </div>
        </div>
      </div>

      {template.uploadTargetInfo && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Upload-Ziel
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Anzeige aus der bestehenden HubSpot-/Drive-Infrastruktur. Diese Maske pflegt die Links nicht selbst.
              </p>
            </div>
            <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-900 dark:text-gray-300">
              {template.uploadTargetInfo.source === "hubspot" ? "Quelle: HubSpot" : "Quelle: bestehende Drive-Struktur"}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4">
            {renderUploadTarget(template.uploadTargetInfo.projectFolder)}
          </div>
        </div>
      )}

      {votPhotoErrors.__global && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {votPhotoErrors.__global}
        </div>
      )}

      {draft.sections.map((section) => {
        const progress = getSectionProgress(payload, section);
        const isExpanded = !!expandedSections[section.id];
        const visibleItems = section.items.filter((item) =>
          isVisibleInCurrentPayload(section.id, item.id)
        );
        const isSectionChecked =
          visibleItems.length > 0 && visibleItems.every((item) => item.checked);
        return (
          <div key={section.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="flex w-full items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-left dark:border-gray-700 dark:bg-gray-900/40"
            >
              <span className="min-w-0">
                <span className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {section.code}
                </span>
                <span className="mt-1 block text-base font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </span>
                {section.description && (
                  <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">
                    {section.description}
                  </span>
                )}
              </span>
              <span
                className={`text-gray-500 transition-transform dark:text-gray-400 ${
                  isExpanded ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <div className="px-4 py-3 border-b dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={isSectionChecked}
                  disabled={isReadOnly}
                  onChange={(event) => setSectionCompleted(section.id, event.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {section.bulkToggleLabel || "Alle Unterpunkte"}
              </label>
              <div className="text-right">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                    progress.isComplete
                      ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                  }`}
                >
                  {progress.isComplete ? "vollständig" : "unvollständig"}
                </span>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {progress.completed} / {progress.total}
                </p>
              </div>
            </div>
            {renderVotPhotoControls(section)}

            {isExpanded && (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleItems.map((item) => {
                  const completed = isChecklistItemCompleted(item);
                  const isMissingHighlighted =
                    showMissingRequiredHighlights && missingRequiredItemIds.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`p-4 ${
                        isMissingHighlighted
                          ? "bg-red-50/80 outline outline-2 -outline-offset-2 outline-red-500 dark:bg-red-950/20"
                          : ""
                      }`}
                    >
                      {isDirectChoiceItem(item) ? (
                        <div>
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {item.label} {item.required ? "*" : ""}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                            {completed ? "Erfüllt" : item.required ? "Pflichtpunkt" : "Optional"}
                          </span>
                        </div>
                      ) : (
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            disabled={isReadOnly}
                            onChange={(event) =>
                              setItem(section.id, item.id, (current) => ({
                                ...current,
                                checked: event.target.checked,
                              }))
                            }
                            className={`mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                              isMissingHighlighted ? "ring-2 ring-red-500" : ""
                            }`}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900 dark:text-white">
                              {item.label} {item.required ? "*" : ""}
                            </span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                              {completed ? "Erfüllt" : item.required ? "Pflichtpunkt" : "Optional"}
                            </span>
                          </span>
                        </label>
                      )}

                      {renderItemInput(section.id, item, isMissingHighlighted)}

                      {isMissingHighlighted && (
                        <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-300">
                          Pflichtfeld fehlt.
                        </p>
                      )}

                      <textarea
                        value={item.comment || ""}
                        disabled={isReadOnly}
                        onChange={(event) =>
                          setItem(section.id, item.id, (current) => ({
                            ...current,
                            comment: event.target.value,
                          }))
                        }
                        rows={2}
                        className="mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
                        placeholder="Bemerkung optional"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Vollständigkeit & Freigabe
        </h3>

        {missingRequiredItems.length > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <p className="font-medium">Fehlende Pflichtpunkte</p>
            <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
              {missingRequiredItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
              <p className="font-medium">Alle Pflichtpunkte sind erfüllt.</p>
              </div>
            )}

            {storedRecord?.completedAt && (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                <p>
                  Abgeschlossen am {new Date(storedRecord.completedAt).toLocaleString("de-DE")}
                  {(storedRecord.completedByEmail ?? "").trim() ? ` von ${(storedRecord.completedByEmail ?? "").trim()}` : ""}.
                </p>
            {storedRecord.approvedAt && (
              <p className="mt-1">
                Freigegeben am {new Date(storedRecord.approvedAt).toLocaleString("de-DE")}
                {storedRecord.approvedByEmail ? ` von ${storedRecord.approvedByEmail}` : ""}.
              </p>
            )}
          </div>
        )}

        {statusAction && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => requestStatusChange(statusAction.nextStatus)}
              disabled={isReadOnly || isSavingStatus || statusAction.disabled}
              className={`min-h-[48px] rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${statusAction.className}`}
            >
              {isSavingStatus && statusAction.nextStatus === "approved"
                ? "Wird abgeschlossen..."
                : statusAction.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjektdokumentationWizard;
